import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatBytes, formatDateTime, titleCase } from "../../lib/format";
import type { KYCApplication } from "../../lib/types";
import {
  Button,
  ErrorNotice,
  Field,
  PageHeader,
  SelectField,
  Sheet,
  SheetHeader,
  Skeleton,
  StatusPill,
} from "../../components/ui";

const DOCUMENT_TYPES = [
  { value: "certificate_of_incorporation", label: "Certificate of incorporation" },
  { value: "cr14_directors", label: "CR14 — register of directors" },
  { value: "cr6_registered_address", label: "CR6 — registered address" },
  { value: "tax_clearance_itf263", label: "Tax clearance (ITF263)" },
  { value: "proof_of_business_address", label: "Proof of business address" },
  { value: "director_id", label: "Director identity document" },
  { value: "bank_confirmation_letter", label: "Bank confirmation letter" },
];

export function Verification() {
  const { refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<unknown>(null);

  const application = useQuery({
    queryKey: ["kyc"],
    queryFn: () => api.get<KYCApplication>("/api/dashboard/kyc"),
  });

  const submit = useMutation({
    mutationFn: () => api.post<KYCApplication>("/api/dashboard/kyc/submit"),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["kyc"] });
      refreshProfile();
    },
    onError: setError,
  });

  if (application.isLoading) {
    return (
      <Sheet>
        <Skeleton rows={10} />
      </Sheet>
    );
  }
  if (application.error) return <ErrorNotice error={application.error} />;

  const app = application.data!;
  const editable = app.status === "draft" || app.status === "info_requested" || app.status === "rejected";

  return (
    <>
      <PageHeader
        eyebrow="Account"
        title="Business verification"
        description="Zimbabwean regulation requires us to know who we settle money to. Once approved, you can issue live keys and accept real payments."
        actions={<StatusPill status={app.status} />}
      />

      {app.decision_reason && (
        <div
          className={`settle mb-6 rounded-md border px-4 py-3 ${
            app.status === "rejected" || app.status === "info_requested"
              ? "border-sienna/25 bg-sienna-wash"
              : "border-verdigris/25 bg-verdigris-wash"
          }`}
        >
          <p className="label mb-1">Message from our team</p>
          <p className="text-[13px] leading-relaxed text-ink">{app.decision_reason}</p>
        </div>
      )}

      {app.status === "approved" && (
        <div className="settle mb-6 rounded-md border border-verdigris/25 bg-verdigris-wash px-4 py-3">
          <p className="text-[13px] font-medium text-verdigris">
            Verified{app.reviewed_at ? ` on ${formatDateTime(app.reviewed_at)}` : ""}. You can now issue
            live API keys.
          </p>
        </div>
      )}

      <ErrorNotice error={error} className="mb-6" />

      <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
        <div className="space-y-6">
          <BusinessDetails app={app} editable={editable} />
          <Directors app={app} editable={editable} />
          <Documents app={app} editable={editable} />
        </div>

        <div className="space-y-6">
          <Sheet className="settle settle-3 overflow-hidden">
            <SheetHeader title="What we still need" />
            {app.missing_documents.length === 0 && app.directors.length > 0 ? (
              <p className="px-5 py-4 text-[13px] text-verdigris">
                Everything required is on file.
              </p>
            ) : (
              <ul className="divide-y divide-rule">
                {app.directors.length === 0 && (
                  <li className="px-5 py-2.5 text-[13px] text-ink-muted">
                    At least one director or beneficial owner
                  </li>
                )}
                {app.missing_documents.map((doc) => (
                  <li key={doc} className="px-5 py-2.5 text-[13px] text-ink-muted">
                    {DOCUMENT_TYPES.find((d) => d.value === doc)?.label ?? titleCase(doc)}
                  </li>
                ))}
              </ul>
            )}

            {editable && (
              <div className="border-t border-rule px-5 py-4">
                <Button
                  variant="primary"
                  className="w-full"
                  disabled={!app.can_submit}
                  loading={submit.isPending}
                  onClick={() => submit.mutate()}
                >
                  Submit for review
                </Button>
                {!app.can_submit && (
                  <p className="mt-2 text-[11.5px] leading-relaxed text-ink-muted">
                    Complete the sections above before submitting.
                  </p>
                )}
              </div>
            )}
          </Sheet>

          <Sheet className="settle settle-4 overflow-hidden">
            <SheetHeader title="What happens next" />
            <ol className="space-y-3.5 px-5 py-4">
              {[
                "You submit your business details and documents.",
                "An operator at Bushveld reviews them, usually within two business days.",
                "On approval you can issue live keys and start taking real payments.",
              ].map((step, i) => (
                <li key={i} className="flex gap-3 text-[13px] leading-relaxed text-ink-muted">
                  <span className="mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border border-rule-strong font-mono text-[10px]">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </Sheet>
        </div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */

function BusinessDetails({ app, editable }: { app: KYCApplication; editable: boolean }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    business_type: app.business_type,
    industry_code: app.industry_code,
    line1: app.registered_address?.Line1 ?? "",
    city: app.registered_address?.City ?? "",
    province: app.registered_address?.Province ?? "",
    account_name: app.settlement_account?.account_name ?? "",
    account_number: "",
    bank_name: app.settlement_account?.bank_name ?? "",
    branch_code: "",
    volume: String(app.estimated_monthly_volume_minor / 100 || ""),
  });

  const save = useMutation({
    mutationFn: () =>
      api.put<KYCApplication>("/api/dashboard/kyc", {
        business_type: form.business_type,
        industry_code: form.industry_code,
        registered_address: {
          line1: form.line1,
          line2: "",
          city: form.city,
          province: form.province,
          country: "ZW",
        },
        settlement_account: {
          account_name: form.account_name,
          account_number: form.account_number,
          bank_name: form.bank_name,
          branch_code: form.branch_code,
          swift_code: "",
          currency: "USD",
        },
        estimated_monthly_volume_minor: Math.round(Number(form.volume || 0) * 100),
        estimated_volume_currency: "USD",
        directors: app.directors.map((d) => ({
          id: d.id,
          full_name: d.full_name,
          national_id: d.national_id,
          nationality: d.nationality,
          ownership_percent: d.ownership_percent,
          is_pep: d.is_pep,
        })),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["kyc"] }),
  });

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Sheet className="settle settle-1 overflow-hidden">
      <SheetHeader
        title="Business and settlement"
        description="Where you are registered, and the account we settle into"
        actions={
          editable ? (
            <Button size="sm" variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          ) : undefined
        }
      />
      <div className="px-5 py-4">
        <ErrorNotice error={save.error} className="mb-4" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Business type"
            value={form.business_type}
            onChange={set("business_type")}
            disabled={!editable}
            placeholder="private_limited_company"
          />
          <Field
            label="Industry"
            value={form.industry_code}
            onChange={set("industry_code")}
            disabled={!editable}
            placeholder="retail"
          />
          <Field
            label="Registered address"
            value={form.line1}
            onChange={set("line1")}
            disabled={!editable}
            className="sm:col-span-2"
            placeholder="12 Samora Machel Avenue"
          />
          <Field label="City" value={form.city} onChange={set("city")} disabled={!editable} placeholder="Harare" />
          <Field
            label="Province"
            value={form.province}
            onChange={set("province")}
            disabled={!editable}
            placeholder="Harare"
          />
          <Field
            label="Expected monthly volume (USD)"
            type="number"
            value={form.volume}
            onChange={set("volume")}
            disabled={!editable}
            className="sm:col-span-2"
            hint="A rough figure is fine. It helps us set sensible limits."
          />
        </div>

        <div className="rule-t mt-5 pt-5">
          <p className="label mb-3">Settlement account</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Account name"
              value={form.account_name}
              onChange={set("account_name")}
              disabled={!editable}
              className="sm:col-span-2"
              hint="Must match your registered company name."
            />
            <Field
              label="Account number"
              value={form.account_number}
              onChange={set("account_number")}
              disabled={!editable}
              placeholder={app.settlement_account?.account_number_masked || "Account number"}
              hint={
                app.settlement_account?.account_number_masked
                  ? `Currently ${app.settlement_account.account_number_masked}. Leave blank to keep it.`
                  : undefined
              }
            />
            <Field label="Bank" value={form.bank_name} onChange={set("bank_name")} disabled={!editable} placeholder="Your bank" />
            <Field
              label="Branch code"
              value={form.branch_code}
              onChange={set("branch_code")}
              disabled={!editable}
            />
          </div>
        </div>
      </div>
    </Sheet>
  );
}

function Directors({ app, editable }: { app: KYCApplication; editable: boolean }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [director, setDirector] = useState({
    full_name: "",
    national_id: "",
    ownership_percent: "",
    is_pep: false,
  });

  const save = useMutation({
    mutationFn: () =>
      api.put<KYCApplication>("/api/dashboard/kyc", {
        business_type: app.business_type,
        industry_code: app.industry_code,
        registered_address: {
          line1: app.registered_address?.Line1 ?? "",
          line2: app.registered_address?.Line2 ?? "",
          city: app.registered_address?.City ?? "",
          province: app.registered_address?.Province ?? "",
          country: "ZW",
        },
        settlement_account: {
          account_name: app.settlement_account?.account_name ?? "",
          account_number: "",
          bank_name: app.settlement_account?.bank_name ?? "",
          branch_code: "",
          swift_code: "",
          currency: "USD",
        },
        estimated_monthly_volume_minor: app.estimated_monthly_volume_minor,
        estimated_volume_currency: app.estimated_volume_currency,
        directors: [
          ...app.directors.map((d) => ({
            id: d.id,
            full_name: d.full_name,
            national_id: d.national_id,
            nationality: d.nationality,
            ownership_percent: d.ownership_percent,
            is_pep: d.is_pep,
          })),
          {
            full_name: director.full_name,
            national_id: director.national_id,
            nationality: "ZW",
            ownership_percent: Number(director.ownership_percent || 0),
            is_pep: director.is_pep,
          },
        ],
      }),
    onSuccess: () => {
      setAdding(false);
      setDirector({ full_name: "", national_id: "", ownership_percent: "", is_pep: false });
      queryClient.invalidateQueries({ queryKey: ["kyc"] });
    },
  });

  return (
    <Sheet className="settle settle-2 overflow-hidden">
      <SheetHeader
        title="Directors and beneficial owners"
        description="Everyone who owns or controls the business"
        actions={
          editable && !adding ? (
            <Button size="sm" onClick={() => setAdding(true)}>
              Add person
            </Button>
          ) : undefined
        }
      />

      {app.directors.length === 0 && !adding ? (
        <p className="px-5 py-6 text-[13px] text-ink-muted">
          No directors recorded yet. At least one is required.
        </p>
      ) : (
        <ul className="divide-y divide-rule">
          {app.directors.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-4 px-5 py-3">
              <div className="min-w-0">
                <p className="truncate text-[13.5px] text-ink">{d.full_name}</p>
                <p className="mt-0.5 font-mono text-[11.5px] text-ink-faint">
                  {d.national_id || "No ID recorded"}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {d.is_pep && (
                  <span className="rounded-full bg-brass-wash px-2 py-0.5 text-[10.5px] font-medium text-brass">
                    PEP
                  </span>
                )}
                <span className="font-mono text-[12px] tabular text-ink-muted">
                  {d.ownership_percent}%
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div className="border-t border-rule bg-paper-sunk/40 px-5 py-4">
          <ErrorNotice error={save.error} className="mb-4" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Full name"
              value={director.full_name}
              onChange={(e) => setDirector((d) => ({ ...d, full_name: e.target.value }))}
              required
            />
            <Field
              label="National ID"
              value={director.national_id}
              onChange={(e) => setDirector((d) => ({ ...d, national_id: e.target.value }))}
              placeholder="63-1234567-A-42"
            />
            <Field
              label="Ownership (%)"
              type="number"
              min={0}
              max={100}
              value={director.ownership_percent}
              onChange={(e) => setDirector((d) => ({ ...d, ownership_percent: e.target.value }))}
            />
            <label className="flex items-end gap-2 pb-2 text-[13px] text-ink">
              <input
                type="checkbox"
                checked={director.is_pep}
                onChange={(e) => setDirector((d) => ({ ...d, is_pep: e.target.checked }))}
                className="h-4 w-4 rounded border-rule-strong accent-verdigris"
              />
              Politically exposed person
            </label>
          </div>
          <div className="mt-4 flex gap-2">
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Add person
            </Button>
            <Button onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </Sheet>
  );
}

function Documents({ app, editable }: { app: KYCApplication; editable: boolean }) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [type, setType] = useState(DOCUMENT_TYPES[0]!.value);
  const [uploadError, setUploadError] = useState<unknown>(null);

  const upload = useMutation({
    mutationFn: (file: File) => {
      const data = new FormData();
      data.append("type", type);
      data.append("file", file);
      return api.upload("/api/dashboard/kyc/documents", data);
    },
    onSuccess: () => {
      setUploadError(null);
      if (fileInput.current) fileInput.current.value = "";
      queryClient.invalidateQueries({ queryKey: ["kyc"] });
    },
    onError: setUploadError,
  });

  // Clear a stale error when the user changes what they are uploading.
  useEffect(() => setUploadError(null), [type]);

  return (
    <Sheet className="settle settle-3 overflow-hidden">
      <SheetHeader title="Documents" description="PDF, JPEG, PNG or HEIC, up to 25 MB each" />

      {app.documents.length === 0 ? (
        <p className="px-5 py-6 text-[13px] text-ink-muted">Nothing uploaded yet.</p>
      ) : (
        <ul className="divide-y divide-rule">
          {app.documents.map((doc) => (
            <li key={doc.id} className="flex items-center justify-between gap-4 px-5 py-3">
              <div className="min-w-0">
                <p className="truncate text-[13px] text-ink">
                  {DOCUMENT_TYPES.find((d) => d.value === doc.type)?.label ?? titleCase(doc.type)}
                </p>
                <p className="mt-0.5 truncate text-[11.5px] text-ink-faint">
                  {doc.filename} · {formatBytes(doc.size_bytes)} · {formatDateTime(doc.uploaded_at)}
                </p>
              </div>
              {/* The content hash is shown because it is what makes the file provable
                  later: the same digest means the same document. */}
              <code
                className="shrink-0 font-mono text-[10.5px] text-ink-faint"
                title={`SHA-256: ${doc.sha256}`}
              >
                {doc.sha256.slice(0, 8)}
              </code>
            </li>
          ))}
        </ul>
      )}

      {editable && (
        <div className="border-t border-rule bg-paper-sunk/40 px-5 py-4">
          <ErrorNotice error={uploadError} className="mb-3" />
          <div className="flex flex-wrap items-end gap-3">
            <SelectField
              label="Document type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              options={DOCUMENT_TYPES}
              className="min-w-[220px] flex-1"
            />
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/heic"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
              }}
              className="hidden"
              id="kyc-file"
              aria-label="Choose a document to upload"
            />
            <Button
              variant="primary"
              loading={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              Choose file
            </Button>
          </div>
        </div>
      )}
    </Sheet>
  );
}
